/**
 * forest_horizon.ts — STAND impostors: the woods from the ring-2 edge to the horizon (2026-09-25,
 * visual-realism R4b). "From any hill the world is a desert with a green disc around you."
 *
 * The flora worker walks a 32 m (≤ 4 km) / 48 m (beyond) lattice around the camera, skipping every chunk
 * the woods' own rings own (the planted trees and their ring-2 impostors, Chebyshev ≤ foliageRing + 2),
 * and keeps a cell where the map's forest (the planter's noise, cover, tree line and keep-outs) says
 * woods — one clump billboard per cell, the biome's large or medium species by density, sized by the
 * stand taper. This side diffs the set and adds / removes billboards on the per-species impostor
 * instancers the woods already use (the same UNLIT albedo + normal atlases, lit by the scene — R4c).
 * A new job only when the camera has moved a quarter cell-ring or the reach changed.
 */
import type { ImpostorInstancer } from './impostor.js';
import type { StandsJob, StandsResult } from '../workers/flora_scatter_worker.js';

export interface ForestHorizonHost {
  /** The species' impostor instancer (null until its atlas is baked — the next job retries). */
  impostor(key: string): ImpostorInstancer | null;
  /** The raw height (m) of the species' baked mesh (billboard scale = target height / raw height), or null. */
  rawHeight(key: string): number | null;
}

export class ForestHorizon {
  private worker: Worker | null = null;
  private broken = false;
  private seq = 0;
  private lastX = Number.NaN; private lastZ = Number.NaN; private lastR = -1; private lastInner = -1;
  private busy = false;
  private readonly live = new Map<string, { inst: ImpostorInstancer; id: number }>(); // "x,z" → billboard
  readonly stats = { jobs: 0, stands: 0, pendingSpecies: 0, lastMs: 0 };

  constructor(private readonly host: ForestHorizonHost) {}

  /** Per frame (cheap): post a stands job when the camera or the reach moved enough. */
  update(camX: number, camZ: number, seed: string, sea: number, rOut: number, innerCheb: number, chunkSize: number): void {
    if (rOut <= 0) { this.clear(); this.lastR = rOut; return; }
    const cell = rOut <= 4000 ? 32 : 48;
    const moved = Math.hypot(camX - this.lastX, camZ - this.lastZ);
    if (this.busy || (moved < Math.max(192, rOut * 0.06) && rOut === this.lastR && innerCheb === this.lastInner && this.stats.pendingSpecies === 0)) return;
    if (!this.worker && !this.broken) {
      try {
        this.worker = new Worker(new URL('../workers/flora_scatter_worker.ts', import.meta.url), { type: 'module' });
        this.worker.onmessage = (e: MessageEvent<StandsResult>) => { this.busy = false; if (e.data.kind === 'stands') this.apply(e.data); };
        this.worker.onerror = () => { this.broken = true; this.busy = false; };
      } catch { this.broken = true; }
    }
    if (!this.worker) return;
    this.lastX = camX; this.lastZ = camZ; this.lastR = rOut; this.lastInner = innerCheb;
    this.busy = true; this.stats.jobs++;
    const job: StandsJob = { kind: 'stands', id: ++this.seq, seed, sea, camX, camZ, rOut, cell, chunkSize, innerCheb };
    this.worker.postMessage(job);
  }

  private apply(r: StandsResult): void {
    this.stats.lastMs = r.ms;
    const want = new Set<string>();
    let pending = 0;
    const d = r.data;
    for (let o = 0; o + 5 <= d.length; o += 5) {
      const x = d[o], y = d[o + 1], z = d[o + 2], key = r.keys[d[o + 3]], height = d[o + 4];
      const k = `${Math.round(x)},${Math.round(z)}`;
      want.add(k);
      if (this.live.has(k)) continue;
      const inst = this.host.impostor(key); const raw = this.host.rawHeight(key);
      if (!inst || !raw) { pending++; continue; }
      const id = inst.add(x, y, z, height / raw);
      if (id >= 0) this.live.set(k, { inst, id });
    }
    for (const [k, h] of this.live) if (!want.has(k)) { h.inst.remove(h.id); this.live.delete(k); }
    this.stats.stands = this.live.size; this.stats.pendingSpecies = pending;
  }

  /**
   * Probe read (`__horizon().bearings`): the seated stands around (x, z) by compass bin — how many, and the bin
   * with the most, as a yaw bearing (radians, atan2(dx, dz)) — so a probe faces the woods it wants to picture.
   */
  bearings(x: number, z: number, bins = 16, minDist = 600): { bins: number[]; best: number; bestYaw: number; medianDist: number } {
    const n = new Array<number>(bins).fill(0); const dists: number[] = [];
    for (const k of this.live.keys()) {
      const c = k.indexOf(','); const dx = Number(k.slice(0, c)) - x, dz = Number(k.slice(c + 1)) - z; const d = Math.hypot(dx, dz);
      if (d < minDist) continue;
      const b = Math.floor(((Math.atan2(dx, dz) / (2 * Math.PI)) + 1) % 1 * bins) % bins; n[b]++; dists.push(d);
    }
    let best = 0; for (let i = 1; i < bins; i++) if (n[i] > n[best]) best = i;
    dists.sort((a, b) => a - b);
    return { bins: n, best, bestYaw: ((best + 0.5) / bins) * 2 * Math.PI, medianDist: dists.length ? dists[dists.length >> 1] : 0 };
  }

  clear(): void { for (const h of this.live.values()) h.inst.remove(h.id); this.live.clear(); this.stats.stands = 0; this.lastX = Number.NaN; }
  dispose(): void { this.clear(); this.worker?.terminate(); this.worker = null; }
}
